import { Controller } from "@hotwired/stimulus"

// FAB + slide-in panel for the in-app Platform chat (data-controller="platform-chat").
// Toggling + Enter-to-submit only — the thread is server-rendered and Turbo-streamed, so this
// stays tiny. No global side effects.
export default class extends Controller {
  static targets = ["panel", "backdrop"]

  toggle() {
    this.panelTarget.classList.contains("is-open") ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.add("is-open")
    this.backdropTarget?.classList.add("is-open")
    this.focusInput()
  }

  close() {
    this.panelTarget.classList.remove("is-open")
    this.backdropTarget?.classList.remove("is-open")
  }

  // Re-focus the composer after the panel opens or the thread re-renders.
  focusInput() {
    requestAnimationFrame(() =>
      this.element.querySelector("textarea[name='content']")?.focus()
    )
  }

  // Cards (approvals, confirmations) are rendered by a background broadcast with no session,
  // so a form token baked in at render time would be blank. Stamp the page's live token at
  // submit time instead.
  stampCsrf(event) {
    const field = event.target.querySelector("input[name='authenticity_token']")
    if (field) field.value = document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  // What the user is looking at goes with every question (window.__PLATFORM__.snapshot, read by
  // Platform::ChatPage). Taken at submit, not render: filters change in place and a
  // turbo_permanent workspace switches pages under a panel that never re-renders.
  // The current-page pill (chat-mention#_pinCurrentPage) removed → "none": the user asked
  // without the page, so the server must not fall back to the Referer and a screenshot either.
  stampPage(event) {
    const form = event.target
    const field = form.querySelector("input[name='page_snapshot']")
    if (!field) return
    if (form.dataset.pagePinned === "true" && !form.querySelector(".laz-page-chip[data-current-page]")) {
      field.value = "none"
      return
    }
    try {
      const shot = window.__PLATFORM__?.snapshot?.()
      field.value = shot ? JSON.stringify(shot) : ""
    } catch (_) {
      field.value = ""
    }
  }

  // Enter submits; Shift+Enter is a newline.
  //
  // event.repeat: holding Enter fires keydown over and over, and each one is a whole message.
  // The in-flight flag covers the rest (a fast second press, a double-tap) — the form is gone
  // by the time the reply lands, so nothing needs to clear it.
  // defaultPrevented: the @ picker (chat-mention, listed first) took this Enter to pick a row.
  submitOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing || event.repeat || event.defaultPrevented) return

    event.preventDefault()
    const form = event.target.closest("form")
    if (!form || form.dataset.submitting === "true") return

    form.dataset.submitting = "true"
    form.requestSubmit()
  }
}
